import { ErrorIntegration } from "@features/tickets";
import { AlertCircle, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils/cn";
import { errorHandler } from "@/lib/utils/errorHandler";

/** Axios-style response payload carried by API errors (structural, never imported). */
interface ErrorResponsePayload {
  status?: number;
  statusText?: string;
  data?: unknown;
}

export interface QueryErrorBoundaryProps {
  /** The query error (any shape — narrowed structurally). */
  error?: unknown;
  /** Function to retry the query. */
  refetch?: () => void;
  /** Additional CSS classes. */
  className?: string;
  /** Optional custom title. */
  title?: string;
}

/** Extracts the Axios-style response from an unknown error without asserting its type. */
function extractResponse(error: unknown): ErrorResponsePayload | undefined {
  if (typeof error !== "object" || error === null || !("response" in error)) {
    return undefined;
  }
  return (error as { response?: ErrorResponsePayload }).response;
}

/**
 * Query-specific error boundary for React Query errors
 */
export function QueryErrorBoundary({
  error,
  refetch,
  className,
  title = "Erreur de chargement",
}: QueryErrorBoundaryProps) {
  // Handle null or undefined error gracefully
  if (!error) {
    return null;
  }

  const response = extractResponse(error);
  const userMessage = errorHandler.getUserMessage(error);

  // Log the query error
  errorHandler.logQueryError(error, {
    type: "Query Error",
    refetchAvailable: !!refetch,
  });

  return (
    <div className={cn("alert alert-error shadow-lg", className)}>
      <div className="flex items-start gap-3 w-full">
        <AlertCircle className="w-6 h-6 flex-shrink-0" strokeWidth={2} />

        <div className="flex-1">
          <h3 className="font-bold">{title}</h3>
          <p className="text-sm mt-1">{userMessage}</p>

          {/* Show technical details only in development */}
          {import.meta.env.DEV && response && (
            <details className="mt-2">
              <summary className="cursor-pointer text-xs opacity-70 hover:opacity-100">
                Détails techniques
              </summary>
              <pre className="mt-1 text-xs overflow-auto max-h-40 bg-base-100/20 p-2 rounded">
                {JSON.stringify(
                  {
                    status: response.status,
                    statusText: response.statusText,
                    data: response.data,
                  },
                  null,
                  2,
                )}
              </pre>
            </details>
          )}
        </div>

        {refetch && (
          <button onClick={refetch} className="btn btn-sm btn-ghost gap-2 flex-shrink-0">
            <RefreshCw className="w-4 h-4" />
            Réessayer
          </button>
        )}
      </div>

      <ErrorIntegration statusCode={response?.status} errorMessage={userMessage} />
    </div>
  );
}
